// =========================
// Generic Modal Styles
// =========================

import { flexCol, flexRowEnd, flexColumnBetween } from "./flex";
import { spaces } from "./spacing";
import { colors } from "./colors";
import { shadows } from "./shadows";
import { zIndexes } from "./zIndexes";

// =========================
// Main modal view
// =========================

export const mainModal = {
  zIndex: zIndexes.z1205,
  maxWidth: 1500
};

export const modalTitleBox = {
  ...flexRowEnd,
  gap: spaces[1],
};

export const modalTitleBox2 = {
  ...flexColumnBetween,
};

export const MainIconModal = {
  fontSize: "60px",
  color: colors.redSubtitle,
  fill: colors.redSubtitle,
};

export const modalMainBox = {
  ...flexCol,
  gap: spaces["2.5"],
};

export const modalSectionBox = {
  ...flexCol,
  gap: spaces["1"],
};

export const statusBoxModal = {
  ...flexCol,
  borderRadius: spaces["0.5"],
  padding: "4px 8px",
  backgroundColor: (theme: any) =>
    theme?.palette?.mode === "dark" ? colors.darkBackground : colors.white,
  boxShadow: (theme: any) =>
    theme?.palette?.mode === "dark" ? "none" : shadows.sh1,
  border: (theme: any) =>
    theme?.palette?.mode === "dark" ? `1px solid ${colors.lightBorderOpacity}` : "none",
};

export const statusBadgeContainer = {
  width: "100%",
  display: "flex",
  alignItems: "stretch",
};

export const statusBadgePanel = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: "42px",
  padding: `${spaces[1.5]} ${spaces[1.75]}`,
  borderRadius: "4px",
  backgroundColor: (theme: any) =>
    theme?.palette?.mode === "dark" ? colors.darkBackground : colors.white,
  transition: "all 0.2s ease-in-out",
};

export const statusBadgeValueRow = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spaces[1],
};

export const statusBadgeValueText = {
  fontFamily: "montserrat",
  fontWeight: 600,
  fontSize: "1rem",
  lineHeight: 0.5,
  letterSpacing: "0.04em",
  color: colors.textPrimary,
  textTransform: "uppercase",
};

export const statusBadgeIcon = {
  color: colors.textPrimary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  backgroundColor: "transparent",
  flexShrink: 0,
  "& svg": {
    width: "24px",
    height: "24px",
    display: "block",
    color: colors.textPrimary,
    stroke: "currentColor",
    fill: "none",
  },
  "& svg path": {
    stroke: "currentColor",
    fill: "none",
  },
  "& svg circle": {
    stroke: "currentColor",
    fill: "none",
  },
};

export const modalNarrow = {
  maxWidth: 560,
};

export const destructiveModalPaper = {
  width: "600px",
  maxWidth: "92vw",
};

export const destructiveModalRoot = {
  ...flexCol,
  gap: spaces[2],
};

export const destructiveModalHeader = {
  display: "grid",
  gridTemplateColumns: "64px 1fr",
  alignItems: "center",
  gap: spaces[1],
};

export const destructiveModalIcon = {
  width: "56px",
  height: "56px",
  color: colors.redSubtitle,
};

export const destructiveModalTitle = {
  fontWeight: 700,
  lineHeight: 1.2,
};

export const destructiveModalSubtitle = {
  color: colors.darkGray,
};

export const destructiveModalSummaryGrid = {
  display: "grid",
  gridTemplateColumns: "140px 1fr",
  gap: spaces[1],
  alignItems: "center",
};

export const destructiveModalSummaryLabel = {
  fontWeight: 600,
  color: colors.darkGray,
};

export const destructiveModalSummaryValue = {
  padding: `${spaces[1]} ${spaces[1.5]}`,
  borderRadius: spaces[0.75],
  border: `2px solid ${colors.btnSave}`,
  backgroundColor: "#3a3a3a",
  color: colors.white,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontWeight: 500,
  fontSize: "0.95rem",
  boxShadow: `0 2px 8px ${colors.darkShadowOpacity40}`,
};

export const destructiveModalDetailsBox = {
  padding: `${spaces[1.5]} ${spaces[2]}`,
  borderRadius: spaces[0.75],
  border: `2px solid ${colors.btnSave}`,
  backgroundColor: "#3a3a3a",
  color: colors.white,
  minHeight: "88px",
  maxHeight: "140px",
  overflow: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontSize: "0.95rem",
  lineHeight: 1.6,
  boxShadow: `0 3px 10px ${colors.darkShadowOpacity40}`,
};

export const destructiveModalActions = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
};

export const destructiveModalErrorText = {
  color: colors.redSubtitle,
  fontSize: "0.9rem",
};

// =========================
// Release notes dialog
// =========================

export const releaseNotesDialogPaper = {
  borderRadius: spaces[1],
};

export const releaseNotesDialogTitle = {
  padding: "16px 20px 8px",
  fontWeight: 600,
};

export const releaseNotesDialogContent = {
  padding: "8px 20px 20px",
  maxHeight: "70vh",
};

export const releaseNotesHeaderMeta = {
  marginBottom: spaces[1],
  color: (theme: any) => theme.palette.text.secondary,
};

export const releaseNotesAccordion = {
  borderRadius: spaces[0.5],
  boxShadow: "none",
  border: (theme: any) => `1px solid ${theme.palette.divider}`,
  marginBottom: spaces[1],
  "&:before": {
    display: "none",
  },
};

export const releaseNotesAccordionSummary = {
  minHeight: "50px",
  "& .MuiAccordionSummary-content": {
    margin: "10px 0",
  },
};

export const releaseNotesVersionText = {
  fontWeight: 600,
};

export const releaseNotesDateText = {
  color: (theme: any) => theme.palette.text.secondary,
  fontSize: "0.8rem",
};

export const releaseNotesHighlightsList = {
  marginTop: spaces[0.5],
  marginBottom: spaces[1],
  paddingLeft: spaces[2],
};

export const releaseNotesSectionBox = {
  marginBottom: spaces[1],
};

export const releaseNotesSectionTitle = {
  fontWeight: 600,
  marginBottom: spaces[0.5],
};

export const releaseNotesItemsList = {
  margin: 0,
  paddingLeft: spaces[2],
};

export const releaseNotesEmptyState = {
  padding: spaces[2],
  textAlign: "center",
  color: (theme: any) => theme.palette.text.secondary,
};

export const releaseNotesDialogActions = {
  padding: "8px 20px 16px",
};

export const releaseNotesTimelineRoot = {
  position: "relative",
  paddingLeft: spaces[2],
};

export const releaseNotesTimelineLine = {
  position: "absolute",
  top: 0,
  bottom: 0,
  left: "14px",
  width: "2px",
  backgroundColor: (theme: any) => theme.palette.divider,
};

export const releaseNotesTimelineItem = {
  position: "relative",
  paddingLeft: "32px",
  marginBottom: spaces[2],
};

export const releaseNotesTimelineDot = {
  position: "absolute",
  left: "8px",
  top: "14px",
  width: "12px",
  height: "12px",
  borderRadius: "50%",
  backgroundColor: (theme: any) => theme.palette.primary.main,
  boxShadow: (theme: any) => `0 0 0 3px ${theme.palette.background.paper}`,
};

export const releaseNotesTimelineCard = {
  borderRadius: spaces[0.5],
  border: (theme: any) => `1px solid ${theme.palette.divider}`,
  overflow: "hidden",
};

export const releaseNotesTimelineHeader = {
  display: "flex",
  alignItems: "baseline",
  gap: spaces[0.5],
};

export const releaseNotesSectionHeader = {
  display: "flex",
  alignItems: "center",
  gap: spaces[0.5],
  marginBottom: spaces[0.5],
};

// =========================
// FlexSpeedDial Styles
// =========================

export const flexSpeedDialSx = {
  position: "relative",
  alignItems: "center",
  "& .MuiFab-primary": {
    width: "48px",
    height: "48px",
    minHeight: "48px",
    backgroundColor: "primary.main",
    color: "primary.contrastText",
    boxShadow: "0 8px 18px rgba(10, 123, 194, 0.24)",
  },
  "& .MuiFab-primary:hover": {
    backgroundColor: "primary.dark",
  },
  "& .MuiSpeedDial-actions": {
    gap: "10px",
    paddingBottom: "0px",
    paddingRight: "42px",
    alignItems: "flex-end",
  },
  "& .MuiSpeedDialAction-fab": {
    width: "42px",
    height: "42px",
    backgroundColor: "background.paper",
    color: "#888888",
    boxShadow: "0 8px 18px rgba(0, 0, 0, 0.16)",
    border: "1px solid",
    borderColor: "divider",
  },
  "& .MuiSpeedDialAction-fab:hover": {
    backgroundColor: "primary.main",
    color: "primary.contrastText",
  },
  "& .MuiSpeedDialAction-fab.Mui-disabled": {
    backgroundColor: "action.disabledBackground",
    color: "action.disabled",
  },
};

export const entityStatusSelectPanelSx = {
  marginTop: "4px",
  paddingTop: "12px",
  borderTop: "1px solid",
  borderColor: "divider",
};

export const flexSpeedDialPreviewPanel = {
  display: "flex",
  flexDirection: "column",
  gap: spaces["1"],
  padding: `${spaces["1.5"]} ${spaces["2"]}`,
  borderRadius: spaces["1"],
  backgroundColor: "background.paper",
  minWidth: "280px",
  maxWidth: "320px",
  boxShadow: shadows.sh1,
};

export const flexSpeedDialPreviewTitle = {
  fontWeight: 600,
  color: "text.primary",
  fontSize: "14px",
};

export const flexSpeedDialPreviewCaption = {
  color: "text.secondary",
  fontSize: "12px",
  lineHeight: "1.4",
};

export const flexSpeedDialActionButtonGroup = {
  marginTop: spaces["1"],
  display: "flex",
  gap: spaces["0.75"],
};

// =========================
// FlexWarningAlert Styles
// =========================

export const warningAlertContainer = {
  ...flexCol,
  alignItems: "center",
  padding: spaces[2],
  gap: spaces[2],
  minWidth: "400px",
  maxWidth: "500px",
};

export const warningAlertIconBox = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: spaces[10],
  height: spaces[10],
  borderRadius: "50%",
};

export const warningAlertTitle = {
  fontFamily: "montserrat",
  fontSize: "18px",
  fontWeight: "600",
  color: colors.redSubtitle,
  textAlign: "center",
};

export const warningAlertContentBox = {
  width: "100%",
  ...flexCol,
  gap: spaces[1.5],
};

export const warningAlertMessage = {
  fontFamily: "montserrat",
  fontSize: "14px",
  fontWeight: "400",
  color: colors.textPrimary,
  whiteSpace: "pre-line",
  textAlign: "center",
  lineHeight: "1.5",
};

export const warningAlertItemBox = {
  display: "flex",
  alignItems: "center",
  gap: spaces[1.5],
  padding: `${spaces[1]} ${spaces[1.5]}`,
  borderRadius: spaces[0.75],
};

export const warningAlertItemIcon = {
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
};

export const warningAlertItemMessage = {
  fontFamily: "montserrat",
  fontSize: "13px",
  lineHeight: "1.4",
  fontWeight: "400",
  flex: 1,
};

export const warningAlertButtonBox = {
  width: "100%",
  ...flexCol,
  alignItems: "center",
  gap: spaces[2],
};
